import { customElement } from "lit/decorators.js";
import { html, render } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import type { DescriptionHeading } from "@/iframe_messages";
import type { ActivityStatusChange } from "@/components/activity_series";
import { type DescriptionFrame, headingHash, headingInHash } from "@/components/description_frame";
import { I18n } from "@/i18n/i18n";
import { isPending, submissionStatusColor, submissionStatusIcon, submissionStatusLabel } from "@/submission_status";
import { ScrollPin, scrollPaddingTop } from "@/scroll_pin";
import { CARD_EASE, prefersReducedMotion } from "@/motion";

// With more headings than this, the list shows the subsections of the section being read only.
const MAX_FULL_LIST = 10;

// How far below the place a link scrolls a heading to it may be and still be the one being read.
const ACTIVE_TOLERANCE = 16;

export interface ListItem {
    id: string;
    text: string;
    depth: number;
}

/**
 * The items of the list: every heading when there are at most MAX_FULL_LIST, otherwise the top level
 * plus the second level of the section the active heading is in, led by the item for the top of the
 * page when there is one. The highest level in the description becomes depth 1. A single item makes
 * a list too, as a way back to the top.
 */
export function listItems(
    headings: DescriptionHeading[],
    activeId: string | null,
    start: ListItem | null = null,
): ListItem[] {
    const top = Math.min(...headings.map((h) => h.level));
    const items = headings.map((h) => ({ id: h.id, text: h.text, depth: h.level - top + 1 }));
    if (items.length <= MAX_FULL_LIST) return start ? [start, ...items] : items;

    let section = -1;
    const sections = items.map((item) => (item.depth === 1 ? ++section : section));
    const activeIndex = items.findIndex((item) => item.id === activeId);
    const shown = items.filter(
        (item, i) =>
            item.depth === 1 || (activeIndex >= 0 && item.depth === 2 && sections[i] === sections[activeIndex]),
    );
    return start ? [start, ...shown] : shown;
}

/**
 * Lists the section headings of the description in the right rail and marks the one being read.
 *
 * The description lives in a sandboxed iframe that d-description-frame sizes to its content, so the
 * page scrolls, not the iframe. The headings arrive with their offset in the description in a
 * description-headings event, and again when they move. The element adds the offset of the iframe
 * on the page to know where each heading is. When the description opens with something else than a
 * heading, the activity title (`data-title`) leads the list and links to the top of the page
 * (`data-top`), so the opening has an item too. The link to the hand-in or read card below the list
 * takes part as the last item, and its status line follows the latest-submission-changed events
 * of d-handin-card and the reads of d-read-card. Each item is known by its address: a heading by the address d-description-frame
 * gives it, so it can share its id with an element of the page, and the other items by the id of
 * their element on the page. A click on a heading goes through d-description-frame; the browser
 * scrolls to the other items itself.
 *
 * Renders the list into the server-rendered `ol.on-this-page-list`; the rest of the rail stays as
 * the server rendered it.
 *
 * @element d-on-this-page
 */
@customElement("d-on-this-page")
export class OnThisPage extends DodonaElement {
    private headings: DescriptionHeading[] = [];
    private intro = false;
    private frame: DescriptionFrame | null = null;
    // The address of the item being read.
    private active: string | null = null;
    private scrollFrame: number | null = null;
    private pin = new ScrollPin(() => this.onPinReleased());
    // The address of the item a click or the address bar scrolled to, while the page has not moved since.
    private clicked: string | null = null;

    private get list(): HTMLOListElement | null {
        return this.querySelector(".on-this-page-list");
    }

    private get jumpLink(): HTMLAnchorElement | null {
        return this.querySelector(".on-this-page-jump-link");
    }

    private get startItem(): ListItem | null {
        const { title, top } = this.dataset;
        return this.intro && title && top ? { id: top, text: title, depth: 1 } : null;
    }

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener("description-headings", this.onHeadings);
        document.addEventListener("latest-submission-changed", this.onLatestSubmission);
        document.addEventListener("activity-status-changed", this.onStatusChanged);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("scrollend", this.onScrollEnd);
        window.addEventListener("resize", this.onScroll, { passive: true });
        window.addEventListener("hashchange", this.onHashChange);
        this.addEventListener("click", this.onClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener("description-headings", this.onHeadings);
        document.removeEventListener("latest-submission-changed", this.onLatestSubmission);
        document.removeEventListener("activity-status-changed", this.onStatusChanged);
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("scrollend", this.onScrollEnd);
        window.removeEventListener("resize", this.onScroll);
        window.removeEventListener("hashchange", this.onHashChange);
        this.removeEventListener("click", this.onClick);
        if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
        this.pin.clear();
    }

    private onHeadings = (event: Event): void => {
        const detail = (event as CustomEvent<{ headings: DescriptionHeading[]; intro?: boolean }>).detail;
        const first = this.frame === null;
        this.headings = detail.headings;
        this.intro = detail.intro ?? false;
        this.frame = event.target as DescriptionFrame;
        // d-description-frame scrolls to the heading the address names once the headings first arrive.
        if (first) this.pinHash();
        this.active = this.activeAddress();
        this.renderList();
    };

    private onHashChange = (): void => {
        this.pinHash();
        this.updateActive();
    };

    // Marks the item the address names like a clicked one, as the page scrolls to it.
    private pinHash(): void {
        const heading = headingInHash();
        const address = heading === null ? location.hash : headingHash(heading);
        if (address !== this.jumpHash && address !== this.startHash && !this.isHeading(heading)) return;
        this.pick(address);
    }

    private pick(address: string): void {
        this.pin.pin(address);
        this.clicked = address;
    }

    // The server rendered the first status line, so it is replaced as a whole.
    private onLatestSubmission = (event: Event): void => {
        const { status } = (event as CustomEvent<{ status: string }>).detail;
        const line = this.querySelector("#on-this-page-status");
        if (!line) return;
        const icon = document.createElement("i");
        icon.className = `mdi mdi-${submissionStatusIcon(status)} mdi-12 colored-${submissionStatusColor(status)}`;
        icon.setAttribute("aria-hidden", "true");
        // The label leads and the icon goes with the status it shows; judging has no label.
        if (isPending(status)) {
            line.replaceChildren(icon, ` ${I18n.t("js.activity_status.judging")}`);
        } else {
            line.replaceChildren(
                `${I18n.t("js.activity_status.latest_submission")} `,
                icon,
                ` ${submissionStatusLabel(status)}`,
            );
        }
    };

    // After a read, the link names the card as read and the line gives the date, as the server renders them.
    private onStatusChanged = (event: Event): void => {
        const { readDate } = (event as CustomEvent<ActivityStatusChange>).detail;
        const label = this.querySelector(".on-this-page-jump-label");
        const line = this.querySelector("#on-this-page-status");
        if (!readDate || !label || !line) return;
        label.replaceChildren(label.querySelector(".visually-hidden") ?? "", ` ${I18n.t("js.activity_status.read")}`);
        line.replaceChildren(I18n.t("js.activity_status.read_on"), document.createElement("br"), readDate);
    };

    private renderList(): void {
        const list = this.list;
        if (!list) return;
        const start = this.startItem;
        const activeHeading = headingInHash(this.active ?? "");
        const items = listItems(this.headings, activeHeading, start);
        const shown = new Set(items.map((item) => item.id));
        // Under a collapsed section the section itself is marked.
        const activeIndex = this.headings.findIndex((h) => h.id === activeHeading);
        // Items are told apart by address, as the title item may share its id with a heading.
        const address = (item: { id: string }): string => (item === start ? this.startHash : headingHash(item.id));
        const markedHeading = this.headings
            .slice(0, activeIndex + 1)
            .reverse()
            .find((h) => shown.has(h.id));
        const marked =
            start && this.active === this.startHash ? this.startHash : markedHeading && headingHash(markedHeading.id);
        // Links to different headings need different names, also when the titles repeat.
        const seen = new Map<string, number>();
        const ordinals = items.map((item) => {
            const n = (seen.get(item.text) ?? 0) + 1;
            seen.set(item.text, n);
            return n;
        });
        /**
         * Keyed by address, so a link keeps its heading while the list changes under a click. A link
         * that repeat moves loses focus, so the focus goes back to it.
         */
        const focused = list.contains(document.activeElement) ? (document.activeElement as HTMLElement) : null;
        const before = this.markedLink?.getBoundingClientRect();
        render(
            repeat(
                items,
                (item) => address(item),
                (item, i) =>
                    html`<li>
                        <a
                            class="on-this-page-link"
                            data-depth=${Math.min(item.depth, 3)}
                            href=${address(item)}
                            aria-current=${address(item) === marked ? "location" : "false"}
                            >${item.text}${
                                ordinals[i] > 1 ? html`<span class="visually-hidden"> ${ordinals[i]}</span>` : ""
                            }</a
                        >
                    </li>`,
            ),
            list,
        );
        if (focused?.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
        if (before) this.slideMarker(before);
        const empty = items.length === 0;
        list.hidden = empty;
        this.querySelector<HTMLElement>(".on-this-page-title")?.toggleAttribute("hidden", empty);
        this.jumpLink?.setAttribute("aria-current", this.active === this.jumpHash ? "location" : "false");
    }

    private get markedLink(): HTMLElement | null {
        return this.list?.querySelector('.on-this-page-link[aria-current="location"]') ?? null;
    }

    /**
     * The marker on the track is drawn by the marked link, so it is in place whatever the layout. A
     * new marked link draws it from where the previous one did and slides it home.
     */
    private slideMarker(from: DOMRect): void {
        const to = this.markedLink?.getBoundingClientRect();
        if (!to?.height || (to.top === from.top && to.height === from.height)) return;
        if (prefersReducedMotion()) return;

        this.markedLink.animate(
            {
                translate: [`0 ${from.top - to.top}px`, "0 0"],
                scale: [`1 ${from.height / to.height}`, "1 1"],
            },
            { duration: 200, easing: CARD_EASE, pseudoElement: "::before" },
        );
    }

    private get jumpHash(): string | null {
        return this.jumpLink?.getAttribute("href") ?? null;
    }

    private get startHash(): string | null {
        const start = this.startItem;
        return start ? `#${start.id}` : null;
    }

    // Where a link or the address scrolls a heading to: below the navbar, by the page's scroll padding.
    private get activeLine(): number {
        return scrollPaddingTop() + ACTIVE_TOLERANCE;
    }

    private get atPageEnd(): boolean {
        return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
    }

    private isHeading(id: string | null): boolean {
        return this.headings.some((heading) => heading.id === id);
    }

    /**
     * The address of the last item whose top has passed the active line, or of the top of the page
     * above the first heading. The card is the last thing on the page, so the page can end before
     * its top gets to the line: once the page is scrolled to its end with the card in view, the
     * card is the item being read.
     */
    private current(): string | null {
        const line = this.activeLine;
        const target = this.jumpHash && document.getElementById(this.jumpHash.slice(1));
        if (target) {
            const top = target.getBoundingClientRect().top;
            if (top <= line || (window.scrollY > 0 && this.atPageEnd && top < window.innerHeight)) return this.jumpHash;
        }
        const iframe = this.frame?.querySelector("iframe");
        if (!iframe) return null;
        const frameTop = iframe.getBoundingClientRect().top;
        let current = this.startHash;
        for (const h of this.headings) {
            if (frameTop + h.top > line) break;
            current = headingHash(h.id);
        }
        return current;
    }

    /**
     * A scroll brings the picked item up to the active line, so the scroll position marks it, unless
     * the page ends before the item gets there.
     */
    private onPinReleased(): void {
        if (!this.atPageEnd) this.clicked = null;
        this.updateActive();
    }

    // The address of the item being read: a pinned or picked one, or else the one at the active line.
    private activeAddress(): string | null {
        return this.pin.id ?? this.clicked ?? this.current();
    }

    private updateActive(): void {
        const address = this.activeAddress();
        if (address === this.active) return;
        this.active = address;
        this.renderList();
    }

    private onScroll = (): void => {
        if (this.pin.id === null) this.clicked = null;
        this.pin.scrolled();
        if (this.scrollFrame !== null) return;
        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateActive();
        });
    };

    private onScrollEnd = (): void => this.pin.scrollEnded();

    /**
     * Marks the clicked item for the length of the scroll it starts, so the list does not flicker
     * through the sections it passes.
     */
    private onClick = (event: MouseEvent): void => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
        const link = (event.target as Element).closest<HTMLAnchorElement>(
            "a.on-this-page-link, a.on-this-page-jump-link",
        );
        const address = link?.getAttribute("href");
        if (!address) return;
        this.pick(address);
        this.updateActive();
        // The browser scrolls to the other items itself, on the page.
        const heading = headingInHash(address);
        if (heading === null || !this.frame) return;
        event.preventDefault();
        this.frame.navigateToHeading(heading);
        // A click from the keyboard has no detail; keyboard users go on reading from the heading.
        if (event.detail === 0) this.frame.focusHeading(heading);
    };
}
